import { ArrowLeft, ArrowUpRight, Trophy, Volume2, VolumeX } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { DnaSequence } from "../components/DnaSequence";
import { Footer } from "../components/Footer";
import { FranchisePicker } from "../components/FranchisePicker";
import { Reveal } from "../components/Reveal";
import { RiotballGame } from "../components/RiotballGame";
import { RiotballPlayoffSlider } from "../components/RiotballPlayoffSlider";
import { ArenaGallery } from "../components/ArenaGallery";
import { RosterSlideshow } from "../components/RosterSlideshow";
import { SeasonStandings } from "../components/SeasonStandings";
import { SectionHeading } from "../components/SectionHeading";
import { toast } from "../components/ui/sonner";
import { riotballTeams } from "../data/riotballTeams";
import { artAssets } from "../data/site";

const bioFormula = [
  {
    title: "SYNAPSE-LOCKED ISOLATE",
    text: "The only supplied formula detail for the 2355 Bio Hack system. Additional ingredient data remains sealed until official release notes arrive.",
  },
  {
    title: "99.4% CELLULAR ABSORPTION RATE",
    text: "The campaign absorption claim is presented as supplied in the official ad concept. Full nutritional and regulatory copy remains pending.",
  },
];

const bioVariants = [
  { id: "initiate", name: "INITIATE TUBE", count: "1 TUBE", price: "120 CREDITS", detail: "Single-cycle field trial for first-time operators.", tubes: 1 },
  { id: "prime-stack", name: "PRIME STACK", count: "3 TUBES", price: "320 CREDITS", detail: "Multi-cycle stack for training blocks and route preparation.", tubes: 3 },
  { id: "apex-case", name: "APEX CASE", count: "6 TUBES", price: "600 CREDITS", detail: "Full team case for long-range operations and squad deployment.", tubes: 6 },
];

const originChapters = [
  {
    label: "CH.01 — THE WALLED CITY",
    title: "New Arcadia, 2035.",
    text: "It began in the summer of 2035, in a New America still reeling from the Great Water Shortage and subsequent societal collapse. In the heart of the walled city of New Arcadia, a heavy corporate boot ground down any semblance of joy. The city, a jewel box of sterile perfection, was enforced by the Alliance League, a ruthless security force led by the Chief Constable.",
  },
  {
    label: "CH.02 — JULY 1ST, 2035",
    title: "A ball and a boombox.",
    text: "On a sweltering July 1st, a group of defiant protesters, disillusioned by the League's draconian curfews, gathered on the lawn of the Grand Capitol Building. They were not armed with weapons, but with a defaced soccer ball and a boombox. They began to play, a simple act of defiance that quickly swelled into a large, spontaneous gathering.",
  },
  {
    label: "CH.03 — THE NIGHT OF THE SHATTERED CONCRETE",
    title: "Five lives. One totem.",
    text: "The Alliance League responded with brutal swiftness. Armored drones descended, and League Shock Troopers, clad in pristine white, moved to crush the \"uprising.\" Cornered between the imposing marble Capitol and the brutalist City Blockade, the protesters fought back with makeshift shields and debris, but were overwhelmed. Dozens were injured, and five lives were lost. Yet as the smoke cleared, the defaced ball — now stained red, the symbol of the resistance — lay surrounded by the wreckage. It became a totem. A movement emerged, channeling the collective grief and rage into something tangible. They called themselves The League of the Fallen, and their chosen game was no longer soccer. It was AL RIOTBALL.",
  },
  {
    label: "CH.04 — THE NAME",
    title: "Riot for the fallen. Ball for the game.",
    text: "The name was chosen in remembrance: \"RIOT\" for the unrest and the lives taken; \"BALL\" for the simple game that started it all.",
  },
  {
    label: "CH.05 — THE GAME AS MIRROR",
    title: "Control the Core.",
    text: "The game was designed to be a mirror of that night. It was played in a confined, urban arena filled with obstacles and debris. Two teams: the Red Force, representing the protesters' resilience, and the White Guard, representing the League's oppression. The objective was to control the Core — a glowing ball scored by being thrown through a hoop at the opponent's end of the field. Physical contact was brutal and encouraged. The game was a public catharsis, a way to reenact the battle and reclaim the narrative.",
  },
  {
    label: "CH.06 — BANNED, THEN BOUGHT",
    title: "The League co-opts the riot.",
    text: "The Alliance League initially tried to ban it, branding it a \"terrorist simulation.\" But the people flocked to the illegal, underground matches held in abandoned warehouses and irradiated subway tunnels. It became too large, too popular. The League, ever pragmatic, realized they couldn't suppress it — so they co-opted it, legalizing a sterilized, corporate version of the game while simultaneously trying to profit from it.",
  },
];

const doloChapters = [
  {
    label: "THE CHAMPION",
    title: "Fifteen years of red.",
    text: "Among the early legends of the legalized AL RIOTBALL, few shone as brightly as Dolo. A cybernetically-enhanced athlete created by the Alliance League, Dolo was the quintessential Red Force player — the embodiment of the Red Force's strength. For 15 years, he dominated the league, a relentless, unyielding force of nature. His chassis, built with advanced alloys and hydraulics, allowed him to absorb incredible punishment and deliver devastating counter-attacks. He was a fan favorite, a symbol that the oppressed could, through sheer will and power, stand against the machine.",
  },
  {
    label: "DECOMMISSIONED — 2350",
    title: "Declared obsolete.",
    text: "But in the cynical world of AL RIOTBALL, there is no room for sentimentality. Dolo's internal processors couldn't keep up with the newer, faster, more agile models introduced by the league's R&D. Five years ago, in 2350, Dolo played his final Championship Final. It was a brutal match, and although the Red Force won, Dolo's chassis was critically damaged. The Alliance League, ever focused on the future and profit, didn't repair him. He was declared \"obsolete,\" decommissioned, and sent to the scrapyard — a hollowed-out husk of a champion. His name was stricken from the record books, replaced by a series of new, AI-driven champions.",
  },
  {
    label: "SALVAGED",
    title: "Kaelen's workshop.",
    text: "Dolo's story didn't end in the scrapheap of history. His remains were discovered by Kaelen, a scavenger from the fringes of Atlanta who knew the value of legendary tech. Kaelen salvaged Dolo's core components and brought them to his subterranean workshop in the ruins of the Old Atlanta Underground. There, in the radioactive murk of 2355, he began to piece Dolo back together — not as a glittering athlete, but as something else entirely. Using scavenged hydraulic systems, patchworked armor plating, and cobbled-together weapons mounts, Kaelen revamped Dolo for Wasteland Running.",
  },
];

const loadout = [
  {
    label: "CARGO HARNESS",
    text: "Built-in, heavily reinforced compartment on his back, capable of carrying high-value payloads.",
  },
  {
    label: "ENHANCED SERVOS",
    text: "Augmented for raw strength — smashes through barricades and leaps the gaps between crumbling high-rises.",
  },
  {
    label: "INTEGRATED WEAPONRY",
    text: "Retractable mono-filament blade on the left forearm, and a shoulder-mounted pneumatic harpoon launcher for gangers and mutated wildlife.",
  },
];

const teams = [
  {
    id: "red-force",
    name: "RED FORCE",
    tag: "THE PROTESTERS' RESILIENCE",
    record: "CHAMPIONS — 2350 FINAL",
    style: "Born from the League of the Fallen. Full-court collisions, zero retreat, engine noise as a weapon.",
    emblem: "/assets/team-logo-red-force.png",
    alt: "Red Force fist and lightning emblem",
  },
  {
    id: "white-guard",
    name: "WHITE GUARDS",
    tag: "THE LEAGUE'S OPPRESSION",
    record: "CORPORATE REBUILD — 2355 ROSTER",
    style: "Pristine white armor, perfect formation discipline. Nobody scores on the Guard twice in one quarter.",
    emblem: "/assets/team-logo-white-guards.png",
    alt: "White Guards shield emblem",
  },
];


/* the ten tour franchises now live in ../data/riotballTeams.js — shared by
   the roster grid, the dossier pages, and the franchise picker */


const matchDetails = [
  { label: "VENUE", value: "SECTOR 9 ZONES — THE AL RIOTBALL ARENA" },
  { label: "BROADCAST", value: "BLACK-OPS NET / CHANNEL 2355" },
  { label: "CHAMPIONSHIP PURSE", value: "500,000 CR + ALLOCATION TIER-1" },
  { label: "KICKOFF", value: "DUSTFALL — 21:00 SHARP" },
];

export default function Riotball() {
  const location = useLocation();
  const [commercialMuted, setCommercialMuted] = useState(true);
  const commercialRef = useRef(null);

  // iPad/Safari + strict desktop autoplay policies need the muted state on the
  // element itself (React's muted prop doesn't reliably land as an attribute)
  useEffect(() => {
    const video = commercialRef.current;
    if (!video) return;
    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute("muted", "");
    const kick = () => video.play().catch(() => {});
    video.addEventListener("canplay", kick, { once: true });
    kick();
    return () => video.removeEventListener("canplay", kick);
  }, []);

  const toggleCommercialSound = () => {
    const video = commercialRef.current;
    setCommercialMuted((prev) => {
      const next = !prev;
      if (video) {
        video.muted = next;
        if (!next) video.play().catch(() => {});
      }
      return next;
    });
  };

  useEffect(() => {
    if (location.hash === "#origins") {
      const el = document.getElementById("origins");
      if (el) window.setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 350);
    }
  }, [location.hash]);

  return (
    <main className="riotball-page" data-testid="riotball-page">
      <section className="riotball-hero" data-testid="riotball-hero">
        <Link to="/" className="back-link" data-testid="riotball-back-link">
          <ArrowLeft size={16} /> BACK TO DOLO'S WORLD
        </Link>
        <Reveal className="riotball-hero-copy" immediate>
          <span>ALLIANCE LEAGUE // EST. 2035</span>
          <h1>AL RIOTBALL</h1>
          <p>
            Alliance League Riotball — the game born from a protest, banned as a terrorist simulation, and sold back to the people who built
            it. This is the official record — from the Night of the Shattered Concrete to the 2355 Championship
            Final.
          </p>
        </Reveal>
        <Reveal className="riotball-commercial" delay={0.08}>
          <span>OFFICIAL BROADCAST // LEAGUE COMMERCIAL — CHANNEL 2355</span>
          <div className="riotball-commercial-frame">
            <video
              ref={commercialRef}
              poster="/assets/riotball-poster.jpg"
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              aria-label="AL Riotball official league commercial — Red Force vs White Guard"
              data-testid="riotball-commercial-video"
            >
              <source src="/assets/riotball-commercial.webm" type="video/webm" />
              <source src="/assets/riotball-commercial.mp4" type="video/mp4" />
            </video>
            <button
              type="button"
              className="video-mute-button"
              onClick={toggleCommercialSound}
              data-testid="riotball-commercial-mute-button"
              aria-label={commercialMuted ? "Unmute AL Riotball commercial" : "Mute AL Riotball commercial"}
              aria-pressed={!commercialMuted}
            >
              {commercialMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
              <span>{commercialMuted ? "MUTED" : "SOUND ON"}</span>
            </button>
          </div>
        </Reveal>
      </section>

      <FranchisePicker />

      <section className="riotball-arena" data-testid="riotball-arena">
        <SectionHeading
          eyebrow="TRAINING ARENA // PLAYABLE TRANSMISSION"
          title="Suit up. Steal the Core. Beat the moving goal."
          text="Pick Red Force or White Guard and battle the defense — 60 seconds, one electronic goal, and a league record waiting to fall."
        />
        <Reveal>
          <RiotballGame />
          <span className="riotball-game-banner rb-glitch">
            <img
              src="/assets/riotball-banner.png"
              alt="AL RIOTBALL — cracked red league banner"
              data-testid="riotball-game-banner"
            />
            <img className="rb-glitch-copy rb-glitch-a" src="/assets/riotball-banner.png" alt="" aria-hidden="true" />
            <img className="rb-glitch-copy rb-glitch-b" src="/assets/riotball-banner.png" alt="" aria-hidden="true" />
          </span>
        </Reveal>
        <Reveal className="riotball-league-banner" delay={0.05}>
          <span>AL RIOTBALL LEAGUE // PLAYOFFS — INFINITE MULTIVERSE</span>
          <RiotballPlayoffSlider />
        </Reveal>
        <Reveal className="riotball-league-banner" delay={0.055}>
          <span>ARENAS OF THE LEAGUE // TEN GROUNDS, ONE CHAMPIONSHIP</span>
          <ArenaGallery />
        </Reveal>
        <SeasonStandings />
        <Reveal className="riotball-league-banner" delay={0.06}>
          <span>ALLIANCE LEAGUE // OFFICIAL PERSONNEL</span>
          <img
            src="/assets/alliance-league-banner.jpg"
            alt="Alliance League official personnel — league leadership, the Chief Constable's commanders, the cybernetic champion and White Guard shock troopers"
            width="1024"
            height="434"
            loading="lazy"
            data-testid="riotball-league-banner-image"
          />
        </Reveal>
      </section>

      <section className="riotball-story" id="origins" data-testid="riotball-origins">
        <SectionHeading
          eyebrow="LEAGUE ARCHIVE // SEALED RECORD"
          title="The origins of AL Riotball: a history of resistance."
        />
        <div className="story-chapters">
          {originChapters.map((chapter, index) => (
            <Reveal className="story-chapter" delay={index * 0.05} key={chapter.label}>
              <span>{chapter.label}</span>
              <h3>{chapter.title}</h3>
              <p>{chapter.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <Reveal className="riotball-hero-banner">
        <img
          src={artAssets.riotballBanner}
          alt="AL Riotball Championship Final 2355 official banner — Red Force vs White Guard"
          data-testid="riotball-hero-banner-image"
        />
      </Reveal>

      <section className="riotball-showdown" data-testid="riotball-showdown">
        <SectionHeading
          eyebrow="THE FINAL — 2355"
          title="Red Force vs White Guard."
          text="Two teams, one Core, no second whistle. The same battle as 2035 — played under lights now."
        />
        <div className="riotball-teams">
          {teams.map((team, index) => (
            <Reveal className={`riotball-team riotball-team-${team.id}`} delay={index * 0.1} key={team.id}>
              <img src={team.emblem} alt={team.alt} data-testid={`riotball-team-emblem-${team.id}`} />
              <span>{team.tag}</span>
              <h3>{team.name}</h3>
              <strong className="riotball-record">{team.record}</strong>
              <p>{team.style}</p>
            </Reveal>
          ))}
          <span className="riotball-vs" aria-hidden="true">VS</span>
        </div>
      </section>

      <section className="riotball-showdown riotball-league-roster" data-testid="riotball-league-roster">
        <SectionHeading
          eyebrow="THE TOUR // FULL ROSTER — 2355 SEASON"
          title="Ten franchises. One Core."
          text="Every registered franchise on the 2355 tour — from the champion Red Force to the expansion Verdigris Vanguard. The separated emblem plates are the league's official logo templates."
        />
        <div className="riotball-league-grid">
          {riotballTeams.map((team, index) => (
            <Reveal className="riotball-team league-team-card" delay={index * 0.05} key={team.id}>
              <Link to={`/riotball/team/${team.id}`} className="league-team-link" data-testid={`league-team-link-${team.id}`}>
                <img src={`/assets/team-logo-${team.id}.png`} alt={team.alt} data-testid={`league-team-emblem-${team.id}`} />
                <span>{team.tag}</span>
                <h3>{team.name}</h3>
                <strong className="riotball-record">{team.record}</strong>
                <p>{team.style}</p>
                <em className="league-team-cta">OPEN DOSSIER →</em>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="riotball-roster" data-testid="riotball-roster-gallery">
        <SectionHeading
          eyebrow="ROSTER TRANSMISSION // 2355 SEASON"
          title="Key players of the championship league."
          text="Official League schematics, key-player plates, and the season posters from 2313 to now — one tall transmission, shuffled on every visit. No two replays are the same."
        />
        <Reveal>
          <RosterSlideshow />
        </Reveal>
      </section>

      <section className="riotball-details" data-testid="riotball-details">
        <SectionHeading
          eyebrow="MATCH PROTOCOL"
          title="Final transmission details."
        />
        <div className="riotball-detail-grid">
          {matchDetails.map((item, index) => (
            <Reveal className="riotball-detail-cell" delay={index * 0.06} key={item.label}>
              <small>{item.label}</small>
              <strong>{item.value}</strong>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="riotball-story" data-testid="riotball-dolo">
        <SectionHeading
          eyebrow="PERSONNEL FILE // STRICKEN FROM RECORD"
          title="Dolo: from champion to survivor."
        />
        <div className="story-chapters">
          {doloChapters.map((chapter, index) => (
            <Reveal className="story-chapter" delay={index * 0.05} key={chapter.label}>
              <span>{chapter.label}</span>
              <h3>{chapter.title}</h3>
              <p>{chapter.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="riotball-lore" data-testid="riotball-lore">
        <Reveal className="riotball-lore-panel">
          <span>
            <Trophy size={13} /> THE WASTELAND RUNNER — 2355
          </span>
          <h2>Reborn as The Runner.</h2>
          <p>
            Dolo's new chassis is an intimidating fusion of former glory and brutal wasteland ingenuity. His
            distinctive red plating is scarred and patched with mismatched metal; his cybernetic eyes, once bright
            blue, now glow a menacing red. Runners are freelance couriers, smugglers, and mercenaries who navigate
            the most irradiated sectors of the city — the lifeblood of the underground economy, delivering
            contraband, tech, and medical supplies to the forgotten communities of ATL where League drones won't
            dare to fly. Dolo runs with a silent, burning vendetta against the Alliance League that created and
            discarded him — a ghost of a champion, fighting a war of attrition for the soul of the city. His
            delivery runs in the dead of night are the only light many of Atlanta's forgotten citizens ever see.
          </p>
          <div className="riotball-loadout-grid">
            {loadout.map((item) => (
              <div className="riotball-loadout-cell" data-testid={`loadout-${item.label.toLowerCase().replaceAll(" ", "-")}`} key={item.label}>
                <small>{item.label}</small>
                <p>{item.text}</p>
              </div>
            ))}
          </div>
          <div className="riotball-lore-actions">
            <Link to="/enlist" className="button button-primary" data-testid="riotball-enlist-link">
              ENLIST FOR GATE ACCESS
            </Link>
            <Link to="/character-book" className="text-link" data-testid="riotball-dolo-dossier-link">
              OPEN DOLO'S DOSSIER
            </Link>
          </div>
        </Reveal>
      </section>
      <section className="biohack-formula-section" id="bio-hack-section" data-testid="riotball-biohack-section">
        <SectionHeading
          eyebrow="2355 PERFORMANCE SYSTEM // OFFICIAL AL RIOTBALL FUEL"
          title="2355 BIO HACK — the official sports protein in the tube."
          text="Hack your body with Bio Hack. Synapse-Locked Isolate is presented through a sealed tube system built around the campaign’s 99.4% cellular absorption claim."
        />
        <div className="biohack-inline-hero">
          <Reveal className="biohack-page-visual" delay={0.08}>
            <DnaSequence />
            <div className="bio-product-vial" data-testid="biohack-showcase-tube">
              <span className="vial-cap vial-cap-top" />
              <span className="vial-body" />
              <span className="vial-core" />
              <span className="vial-cap vial-cap-bottom" />
            </div>
          </Reveal>
          <div className="biohack-formula-grid">
            {bioFormula.map((item, index) => (
              <Reveal className="biohack-formula-card" delay={index * 0.08} key={item.title}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="biohack-variants-section" data-testid="biohack-variants-section">
        <SectionHeading
          eyebrow="TUBE VARIANTS / DRAFT PRICING"
          title="Choose the deployment format."
          text="In-universe draft pricing is active until the final store URL and checkout details are supplied."
        />
        <div className="biohack-variant-grid">
          {bioVariants.map((variant, index) => (
            <Reveal className="biohack-variant-card" delay={index * 0.08} key={variant.id}>
              <div className={`variant-vial variant-vial-${variant.id}`} aria-hidden="true">
                {Array.from({ length: variant.tubes }, (_, tubeIndex) => (
                  <div className={`variant-tube variant-tube-${variant.id}`} key={tubeIndex} style={{ animationDelay: `${tubeIndex * 0.35}s` }}>
                    <span className="vial-cap vial-cap-top" />
                    <span className="vial-body" />
                    <span className="vial-core" />
                    <span className="vial-cap vial-cap-bottom" />
                  </div>
                ))}
              </div>
              <span>{variant.count}</span>
              <h3>{variant.name}</h3>
              <strong>{variant.price}</strong>
              <p>{variant.detail}</p>
              <button
                type="button"
                onClick={() => toast.info(`${variant.name} ORDER ROUTE PENDING FINAL STORE URL`)}
                data-testid={`biohack-order-${variant.id}`}
              >
                INITIATE GENE-LINK <ArrowUpRight size={15} />
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="biohack-poster-section" data-testid="biohack-poster-section">
        <Reveal className="variant-gallery biohack-poster-gallery" delay={0.1}>
          <div className="variant-gallery-header">
            <span>MULTIVERSE ARCHIVE</span>
            <strong>OFFICIAL KEY ART</strong>
          </div>
          <figure className="variant-cover-card" data-testid="biohack-multiverse-poster">
            <img src={artAssets.multiversePoster} alt="Dolo Wasteland Runner Infinite Multiverse poster artwork" />
            <figcaption>INFINITE MULTIVERSE</figcaption>
          </figure>
        </Reveal>
      </section>

      <Footer />
    </main>
  );
}
